import { normalizedIncludes } from '../../utils/textSearch';
import { isValidHolidayPeriod } from '../holidays/holidayCalendar';

export const employeeId = employee => {
  const value = typeof employee === 'object' ? employee?.id ?? employee?.['@id'] : employee;
  return value == null ? null : String(value).trim().replace(/\/+$/, '').split('/').pop() || null;
};

export const employeeName = employee => typeof employee?.fullName === 'string' && employee.fullName.trim() ||
  [employee?.name, employee?.lastName].filter(value => typeof value === 'string' && value.trim()).join(' ').trim() || 'Empleado';

export const matchesTeamEmployee = (employee, filters) => normalizedIncludes(employeeName(employee), filters.name) &&
  (!filters.department || String(employee.job?.department?.id) === String(filters.department));

// Pending requests are listed whatever their dates; the dates narrow the other statuses.
const datesApply = filters => filters.status !== 'pending';

// Keep ancestors even when only a descendant matches a filter.
export function filterTeam(nodes, filters) {
  const byDates = datesApply(filters);
  return nodes.flatMap(node => {
    const isTeamLeader = node.isTeamLeader ?? node.children.length > 0;
    const children = filterTeam(node.children, filters);
    const matchesEmployee = matchesTeamEmployee(node.employee, filters);
    const holidays = matchesEmployee ? node.holidays.filter(holiday =>
      isValidHolidayPeriod(holiday.fromDate, holiday.toDate) &&
      (!filters.status || holiday.status === filters.status) &&
      (!byDates || !filters.from || holiday.fromDate?.slice(0, 10) >= filters.from) &&
      (!byDates || !filters.to || holiday.toDate?.slice(0, 10) <= filters.to)
    ) : [];
    return holidays.length || children.length ? [{ ...node, isTeamLeader, children, holidays }] : [];
  });
}

// Only filtered paths are expanded, so unrelated teams stay out of the result.
export function teamSearchExpansion(nodes, name) {
  const expanded = new Set();
  if (!name?.trim()) return expanded;
  const visit = (list, path = '') => list.forEach(node => {
    if (!node.children.length) return;
    const key = `${path}/${employeeId(node.employee)}`;
    expanded.add(key);
    visit(node.children, key);
  });
  visit(nodes);
  return expanded;
}

// Historical day values must not prevent loading the employee's requests.
// Preserve signed values from the API; non-numeric values do not enter totals.
export const holidayDays = holiday => {
  const value = holiday?.daysConsumed;
  if (typeof value !== 'number' && typeof value !== 'string') return 0;
  const days = Number(value);
  return Number.isFinite(days) ? days : 0;
};

export function approvedTeamDays(nodes) {
  const seen = new Set();
  const sum = list => list.reduce((total, node) => total + sum(node.children) + node.holidays.reduce((days, holiday) => {
    const key = String(holiday.id);
    if (seen.has(key)) return days;
    seen.add(key);
    return days + (holiday.status === 'approved' ? holidayDays(holiday) : 0);
  }, 0), 0);
  return sum(nodes);
}

export function flattenTeam(nodes, expanded, path = '', depth = 0) {
  return nodes.flatMap(node => {
    const key = `${path}/${employeeId(node.employee)}`;
    const holidays = node.holidays.length ? node.holidays : [null];
    const rows = holidays.map((holiday, index) => ({
      key: `${key}:${holiday?.id ?? index}`, branchKey: key, employee: node.employee,
      holiday, depth, first: index === 0, childCount: node.children.length,
      isTeamLeader: node.isTeamLeader ?? node.children.length > 0,
    }));
    return expanded.has(key) ? [...rows, ...flattenTeam(node.children, expanded, key, depth + 1)] : rows;
  });
}
